import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Check, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { SearchableCategorySelect, type Categoria } from '@/components/searchable-category-select';
import { FieldError } from '@/components/ui/field-error';

export interface InvoiceFormState {
  tipoDocumento: 'factura' | 'nota_credito' | 'nota_debito' | 'autofactura' | 'boleta_venta';
  isElectronicInvoice: boolean;
  cdc: string;
  invoiceNumber: string;
  timbrado: string;
  timbradoStart: string;
  timbradoEnd: string;
  issueDate: string;
  expiringDate: string;
  condition: 'contado' | 'credito';
  currency: string;
  senderRuc: string;
  senderRazonSocial: string;
  receiverRuc: string;
  receiverRazonSocial: string;
  subtotalBeforeTax: string;
  totalDiscounts: string;
  taxExempt: string;
  tax5: string;
  tax10: string;
  grandTotal: string;
  naturalezaOperacion: 'bienes' | 'servicios' | 'mixto' | '';
  categoriaGasto:
    | 'alimentacion'
    | 'transporte'
    | 'salud'
    | 'educacion'
    | 'vivienda'
    | 'servicios'
    | 'insumos'
    | 'otro'
    | '';
}

interface Props {
  value: InvoiceFormState;
  onChange: (v: InvoiceFormState) => void;
  fieldConfidence?: Record<string, number> | null;
  issues?: Record<string, string[]>;
  /**
   * Dirección de la factura. Controla cómo se renderiza la sección Receptor:
   *   - 'purchase' (default): receptor = tenant (FIJO, no editable).
   *   - 'sale': receptor = cliente (editable, lo leyó el OCR).
   */
  direction?: 'purchase' | 'sale';
  /**
   * Datos del tenant (receiver fijo de la factura). Solo aplica a compras.
   * Para ventas propias (direction='sale') no pasar este prop — el receptor
   * es el cliente y viene del formState (receiverRuc/razonSocial).
   */
  tenantReceiver?: {
    ruc: string;
    razonSocial: string;
  };
  /**
   * Si el LLM leyó un receiver distinto al del tenant. Si llega, se
   * muestra el banner con lo que leyó el OCR vs lo del tenant para que
   * el contador verifique contra la imagen y apruebe/rechace.
   */
  receiverMismatch?: {
    extractedRuc: string | null;
    extractedRazonSocial: string | null;
  };
  /**
   * Callback opcional para reintentar la extracción IA — se muestra como
   * botón dentro del banner "La IA no pudo leer N campos". Si no se pasa,
   * el botón no aparece.
   */
  onReExtract?: () => void;
  /** El reintento está corriendo (muestra spinner / disabled). */
  isReExtracting?: boolean;
  /**
   * Mismatch FUERTE entre la razón social del OCR y la oficial de SET para
   * el RUC. Probable causa: el OCR leyó mal el RUC y SET devolvió la razón
   * social de OTRO contribuyente. Si llega, se muestra un banner ROJO con
   * las dos versiones para que el contador verifique contra la foto.
   */
  razonSocialMismatch?: {
    llm: string | null;
    oficial: string | null;
  };
}

/**
 * Indicador compacto de confianza del campo. Usa una píldora chica que
 * entra dentro de la altura de línea del label (leading-4 = 16px < altura
 * del texto del label) — así un label CON badge mide lo mismo que uno SIN
 * badge, y los inputs de la misma fila quedan alineados verticalmente.
 *
 * (El shadcn Badge default es ~22px de alto y desalineaba la fila.)
 */
function ConfidenceBadge({ value }: { value: number | undefined }) {
  if (value === undefined) return null;
  const pct = Math.round(value * 100);
  const cls =
    value >= 0.85
      ? 'bg-success/15 text-success'
      : value >= 0.7
        ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
        : 'bg-destructive/15 text-destructive';
  return (
    <span
      className={`ml-1.5 inline-flex items-center rounded px-1 text-[10px] font-semibold leading-4 align-middle tabular-nums ${cls}`}
    >
      {pct}%
    </span>
  );
}

export function InvoiceForm({
  value,
  onChange,
  fieldConfidence,
  issues = {},
  direction = 'purchase',
  tenantReceiver,
  receiverMismatch,
  onReExtract,
  isReExtracting,
  razonSocialMismatch,
}: Props) {
  const isSale = direction === 'sale';
  const set = <K extends keyof InvoiceFormState>(k: K, v: InvoiceFormState[K]) => {
    onChange({ ...value, [k]: v });
  };

  // Lookup interactivo del RUC emisor contra SET (Marangatu). Se dispara
  // al salir del campo (onBlur). Autocompleta la razón social oficial y
  // muestra si el RUC existe / está activo.
  const [rucLookup, setRucLookup] = useState<{
    status: 'idle' | 'loading' | 'found' | 'inactive' | 'not_found' | 'error';
    estado?: string | null;
  }>({ status: 'idle' });

  // Ref siempre actualizado al value actual — evita stale closure dentro del
  // async lookup (que se dispara on blur del campo RUC pero corre asíncrono).
  // Sin esto, si el usuario edita el RUC y blur antes de que el lookup termine,
  // el spread {...value, senderRazonSocial: oficial} usa la value VIEJA del
  // closure y pisa el RUC nuevo + no actualiza la razón social (porque el
  // padre re-renderiza con la value vieja).
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  async function lookupSenderRuc() {
    const ruc = valueRef.current.senderRuc.trim();
    if (!/^\d{1,8}-\d$/.test(ruc)) {
      setRucLookup({ status: 'idle' });
      return;
    }
    setRucLookup({ status: 'loading' });
    try {
      const r = await api<{
        found: boolean;
        unverified: boolean;
        razonSocial: string | null;
        estado: string | null;
      }>(`/api/admin/invoices/lookup-ruc?ruc=${encodeURIComponent(ruc)}`);
      if (r.unverified) {
        setRucLookup({ status: 'error' });
        return;
      }
      if (!r.found) {
        setRucLookup({ status: 'not_found' });
        return;
      }
      // Autocompletar razón social con la versión oficial de SET. Usamos
      // valueRef (no `value` del closure) para que cualquier edición del
      // usuario entre el blur y el response del SET se preserve.
      if (r.razonSocial) {
        onChange({ ...valueRef.current, senderRazonSocial: r.razonSocial });
      }
      const activo = !r.estado || /activ/i.test(r.estado);
      setRucLookup({ status: activo ? 'found' : 'inactive', estado: r.estado });
    } catch {
      setRucLookup({ status: 'error' });
    }
  }

  // Detectar campos que la IA no pudo leer (vacíos / placeholders) o leyó
  // con baja confianza. Se listan en un banner arriba para que el contador
  // sepa exactamente qué revisar contra la imagen, sin escanear todo el form.
  const reviewFields = computeReviewFields(value, fieldConfidence);

  return (
    <div className="space-y-4">
      {razonSocialMismatch && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="size-4 shrink-0 text-destructive" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-destructive">
                Posible RUC mal leído — verificá la razón social
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                La razón social que leyó la IA NO coincide con la oficial de SET para ese RUC.
                Probable: el OCR equivocó algún dígito del RUC y SET devolvió la razón social
                de OTRO contribuyente. Comprobá contra la foto antes de aprobar.
              </p>
              <div className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                <div className="rounded-md border border-border bg-background p-2">
                  <div className="text-muted-foreground">Razón social leída por OCR</div>
                  <div className="font-mono">{razonSocialMismatch.llm || '—'}</div>
                </div>
                <div className="rounded-md border border-border bg-background p-2">
                  <div className="text-muted-foreground">Razón social oficial de SET</div>
                  <div className="font-mono">{razonSocialMismatch.oficial || '—'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reviewFields.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
                <AlertTriangle className="size-4" />
                La IA no pudo leer {reviewFields.length}{' '}
                {reviewFields.length === 1 ? 'campo con certeza' : 'campos con certeza'}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Revisá estos campos contra la imagen y completá/corregí lo que falte:
              </p>
            </div>
            {onReExtract && (
              <button
                type="button"
                onClick={onReExtract}
                disabled={isReExtracting}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-amber-500/40 bg-background px-3 py-1.5 text-xs font-medium text-amber-700 transition hover:bg-amber-500/10 disabled:opacity-50 dark:text-amber-300"
              >
                {isReExtracting ? <Loader2 className="size-3 animate-spin" /> : <AlertTriangle className="size-3" />}
                {isReExtracting ? 'Procesando…' : 'Reintentar IA'}
              </button>
            )}
          </div>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {reviewFields.map((f) => (
              <li key={f.label}>
                <Badge variant="warning" className="font-normal">
                  {f.label}
                  {f.reason === 'low_confidence' && ' (baja confianza)'}
                  {f.reason === 'empty' && ' (vacío)'}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Section title="Documento">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label required>Tipo</Label>
            <Select
              value={value.tipoDocumento}
              onValueChange={(v) => set('tipoDocumento', v as InvoiceFormState['tipoDocumento'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="factura">Factura</SelectItem>
                <SelectItem value="nota_credito">Nota de crédito</SelectItem>
                <SelectItem value="nota_debito">Nota de débito</SelectItem>
                <SelectItem value="autofactura">Autofactura</SelectItem>
                <SelectItem value="boleta_venta">Boleta de venta</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label required>Condición</Label>
            <Select
              value={value.condition}
              onValueChange={(v) => set('condition', v as 'contado' | 'credito')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contado">Contado</SelectItem>
                <SelectItem value="credito">Crédito</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invoiceNumber" required>
              Nº factura <ConfidenceBadge value={fieldConfidence?.invoice_number} />
            </Label>
            <Input
              id="invoiceNumber"
              placeholder="001-001-0000123"
              value={value.invoiceNumber}
              onChange={(e) => set('invoiceNumber', e.target.value)}
            />
            <FieldError error={issues.invoiceNumber} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="timbrado" required>
              Timbrado <ConfidenceBadge value={fieldConfidence?.timbrado} />
            </Label>
            <Input
              id="timbrado"
              placeholder="12345678"
              value={value.timbrado}
              onChange={(e) => set('timbrado', e.target.value)}
            />
            <FieldError error={issues.timbrado} />
          </div>
          <div className="space-y-1.5">
            <Label>Inicio timbrado</Label>
            <DatePicker
              value={value.timbradoStart}
              onChange={(v) => set('timbradoStart', v)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Fin timbrado</Label>
            <DatePicker value={value.timbradoEnd} onChange={(v) => set('timbradoEnd', v)} />
          </div>
          <div className="space-y-1.5">
            <Label required>Fecha emisión</Label>
            <DatePicker value={value.issueDate} onChange={(v) => set('issueDate', v)} />
            <FieldError error={issues.issueDate} />
          </div>
          <div className="space-y-1.5">
            <Label>Fecha vencimiento</Label>
            <DatePicker
              value={value.expiringDate}
              onChange={(v) => set('expiringDate', v)}
              disabled={value.condition !== 'credito'}
            />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <Checkbox
              checked={value.isElectronicInvoice}
              onCheckedChange={(v) => set('isElectronicInvoice', v === true)}
            />
            Factura electrónica
          </label>
          {value.isElectronicInvoice && (
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="cdc">CDC (44 dígitos)</Label>
              <Input
                id="cdc"
                value={value.cdc}
                onChange={(e) => set('cdc', e.target.value)}
                className="font-mono text-xs"
              />
              <FieldError error={issues.cdc} />
            </div>
          )}
        </div>
      </Section>

      <Section title="Emisor (proveedor)">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="senderRuc" required>
              RUC <ConfidenceBadge value={fieldConfidence?.sender_ruc} />
            </Label>
            <Input
              id="senderRuc"
              placeholder="80012345-0"
              value={value.senderRuc}
              onChange={(e) => {
                set('senderRuc', e.target.value);
                if (rucLookup.status !== 'idle') setRucLookup({ status: 'idle' });
              }}
              onBlur={lookupSenderRuc}
            />
            <FieldError error={issues.senderRuc} />
            {/* Estado del lookup SET (Marangatu) */}
            {rucLookup.status === 'loading' && (
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" /> Consultando SET…
              </p>
            )}
            {rucLookup.status === 'found' && (
              <p className="flex items-center gap-1 text-xs text-success">
                <Check className="size-3" /> RUC válido en SET
                {rucLookup.estado ? ` · ${rucLookup.estado}` : ''}
              </p>
            )}
            {rucLookup.status === 'inactive' && (
              <p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="size-3" /> RUC {rucLookup.estado ?? 'no activo'} — verificar
              </p>
            )}
            {rucLookup.status === 'not_found' && (
              <p className="flex items-center gap-1 text-xs text-destructive">
                <X className="size-3" /> No encontrado en SET
              </p>
            )}
            {rucLookup.status === 'error' && (
              <p className="text-xs text-muted-foreground">No se pudo consultar SET</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="senderRazonSocial" required>
              Razón social
            </Label>
            <Input
              id="senderRazonSocial"
              value={value.senderRazonSocial}
              onChange={(e) => set('senderRazonSocial', e.target.value)}
            />
          </div>
        </div>
      </Section>

      <Section title={isSale ? 'Receptor (cliente)' : 'Receptor (contribuyente)'}>
        {isSale ? (
          /* Venta propia: el receptor es el CLIENTE. Editable para que el
             contador pueda corregir si el OCR lo leyó mal. */
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>RUC cliente</Label>
              <Input
                id="receiverRuc"
                value={value.receiverRuc}
                onChange={(e) => set('receiverRuc', e.target.value)}
                placeholder="0000000-0"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Razón social cliente</Label>
              <Input
                id="receiverRazonSocial"
                value={value.receiverRazonSocial}
                onChange={(e) => set('receiverRazonSocial', e.target.value)}
              />
            </div>
          </div>
        ) : (
          <>
        {/* Compra: receiver NUNCA es editable — es siempre el tenant.
            Si el OCR leyó otro RUC mostramos banner pero los valores
            quedan fijos (mejor UX que input deshabilitado). */}
        {receiverMismatch?.extractedRuc &&
          receiverMismatch.extractedRuc !== (tenantReceiver?.ruc ?? value.receiverRuc) && (
            <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
              <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span className="font-semibold">El OCR leyó otro RUC</span>
              </div>
              <p className="mt-1 text-muted-foreground">
                El extractor detectó RUC{' '}
                <code className="rounded bg-muted px-1 font-mono">
                  {receiverMismatch.extractedRuc}
                </code>
                {receiverMismatch.extractedRazonSocial && (
                  <> ({receiverMismatch.extractedRazonSocial})</>
                )}
                , pero la factura corresponde al contribuyente. Verificá
                contra la imagen: si el OCR se equivocó y la factura SÍ está
                a nombre del contribuyente, podés aprobar normalmente. Si la
                factura no pertenece a este contribuyente, rechazar.
              </p>
            </div>
          )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2 whitespace-nowrap">
              RUC
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Fijo
              </span>
            </Label>
            <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm font-mono">
              {tenantReceiver?.ruc ?? value.receiverRuc}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2 whitespace-nowrap">
              Razón social
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Fijo
              </span>
            </Label>
            <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm">
              {tenantReceiver?.razonSocial ?? value.receiverRazonSocial}
            </div>
          </div>
        </div>
          </>
        )}
      </Section>

      {/* Sección "Ítems" eliminada — para liquidación de IVA/IRP solo
          importan los totales de la sección "Totales" abajo. La imagen
          de la factura se ve en el viewer al lado, por si el contador
          necesita revisar el detalle. */}

      <Section title="Totales">
        <div className="grid grid-cols-2 gap-3 [&_label]:block [&_label]:truncate">
          {/* Moneda ocupa toda la fila — el Select es ancho y no cabe al lado */}
          <div className="col-span-2 space-y-1.5">
            <Label>Moneda</Label>
            <Select value={value.currency} onValueChange={(v) => set('currency', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PYG">PYG (Guaraníes)</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ARS">ARS</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="BRL">BRL</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Subtotal s/IVA</Label>
            <Input
              type="number"
              step="0.01"
              value={value.subtotalBeforeTax}
              onChange={(e) => set('subtotalBeforeTax', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Descuentos</Label>
            <Input
              type="number"
              step="0.01"
              value={value.totalDiscounts}
              onChange={(e) => set('totalDiscounts', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>IVA 5%</Label>
            <Input
              type="number"
              step="0.01"
              value={value.tax5}
              onChange={(e) => set('tax5', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>IVA 10%</Label>
            <Input
              type="number"
              step="0.01"
              value={value.tax10}
              onChange={(e) => set('tax10', e.target.value)}
            />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Exento</Label>
            <Input
              type="number"
              step="0.01"
              value={value.taxExempt}
              onChange={(e) => set('taxExempt', e.target.value)}
            />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>
              Total <ConfidenceBadge value={fieldConfidence?.grand_total} />
            </Label>
            <Input
              type="number"
              step="0.01"
              value={value.grandTotal}
              onChange={(e) => set('grandTotal', e.target.value)}
              className="font-bold text-lg h-11"
            />
          </div>
        </div>
      </Section>

      <Section title="Clasificación">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Naturaleza</Label>
            <Select
              value={value.naturalezaOperacion || 'none'}
              onValueChange={(v) =>
                set('naturalezaOperacion', (v === 'none' ? '' : v) as InvoiceFormState['naturalezaOperacion'])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— sin especificar —</SelectItem>
                <SelectItem value="bienes">Bienes</SelectItem>
                <SelectItem value="servicios">Servicios</SelectItem>
                <SelectItem value="mixto">Mixto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Categoría de gasto</Label>
            <SearchableCategorySelect
              value={(value.categoriaGasto || null) as Categoria | null}
              onChange={(v) =>
                set('categoriaGasto', (v ?? '') as InvoiceFormState['categoriaGasto'])
              }
              placeholder="— sin especificar —"
              nullLabel="— sin especificar —"
              triggerClassName="w-full"
            />
          </div>
        </div>
      </Section>
    </div>
  );
}

/**
 * Detecta qué campos clave la IA dejó vacíos/placeholder o leyó con baja
 * confianza, para listarlos en el banner "Campos a revisar".
 *
 * Dos fuentes de "incertidumbre":
 *   - 'empty': el valor está vacío o es un placeholder (000-000-0000000,
 *     00000000, 0000000-0, "(procesando)", total 0). La IA no lo encontró.
 *   - 'low_confidence': field_confidence < 0.7 — la IA lo leyó pero duda.
 */
function computeReviewFields(
  value: InvoiceFormState,
  fieldConfidence?: Record<string, number> | null
): Array<{ label: string; reason: 'empty' | 'low_confidence' }> {
  const out: Array<{ label: string; reason: 'empty' | 'low_confidence' }> = [];
  const conf = fieldConfidence ?? {};
  const LOW = 0.7;

  // Helpers
  const isEmptyOrPlaceholder = (v: string, placeholder?: string): boolean => {
    const t = (v ?? '').trim();
    if (!t) return true;
    if (placeholder && t === placeholder) return true;
    return false;
  };

  // Campos críticos: (label, valor actual, placeholder, key de confianza)
  const checks: Array<{
    label: string;
    empty: boolean;
    confKey?: string;
  }> = [
    {
      label: 'Nº factura',
      empty: isEmptyOrPlaceholder(value.invoiceNumber, '000-000-0000000'),
      confKey: 'invoice_number',
    },
    {
      label: 'Timbrado',
      empty: isEmptyOrPlaceholder(value.timbrado, '00000000'),
      confKey: 'timbrado',
    },
    { label: 'Fecha emisión', empty: !value.issueDate },
    {
      label: 'RUC emisor',
      empty: isEmptyOrPlaceholder(value.senderRuc, '0000000-0'),
      confKey: 'sender_ruc',
    },
    {
      label: 'Razón social emisor',
      empty: isEmptyOrPlaceholder(value.senderRazonSocial, '(procesando)'),
    },
    {
      label: 'Total',
      empty: !value.grandTotal || Number(value.grandTotal) === 0,
      confKey: 'grand_total',
    },
  ];

  for (const c of checks) {
    if (c.empty) {
      out.push({ label: c.label, reason: 'empty' });
      continue; // empty ya es la alerta más fuerte, no duplicar con low_conf
    }
    if (c.confKey) {
      const cv = conf[c.confKey];
      if (cv !== undefined && cv < LOW) {
        out.push({ label: c.label, reason: 'low_confidence' });
      }
    }
  }

  return out;
}

function Section({
  title,
  children,
  actions,
}: {
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  // Cada sección es una "card" con header propio — agrupa visualmente los
  // campos y separa claramente Documento / Emisor / Receptor / Totales /
  // Clasificación, mucho más fácil de escanear que campos sueltos.
  return (
    <div className="rounded-lg border border-border bg-card/30 overflow-hidden">
      <div className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        {actions}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
